import { getKpisResultados } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, formatPeriodLabel } from "./utils/dateRange";

// Módulo del tab KPIs (Estado de Resultados). El KpisTab lee los filtros
// COMPARTIDOS (`reportsFilters` + `financialReports.filterPeriod`, como los
// demás tabs) y se los pasa explícitos a la action — el módulo no mira el
// rootState para mantener la action testeable y sin acoplarse al shape global.
//
// `kpisData` trae las series MENSUALES de los 4 gráficos del grupo activo del
// filtro central (hoy sólo "ventas"; márgenes/personas/terceros llegan por
// fases), con AMBAS variantes de cada toggle (YoY/Recurrentes/$) — alternar
// un toggle no refetchea.

// Los crecimientos MoM/YoY llegan como fracción (0.2 = 20%, contrato de
// pct_change_or_none); los ratios de flujo ya vienen en escala 0-100. Acá se
// normaliza TODO a escala porcentual para que el chart trate "percent" de una
// sola forma.
function toPercentScale(values) {
    return (values || []).map((value) => (value == null ? null : value * 100));
}

function normalizePersonas(data, monthHeaders) {
    const charts = data.charts || {};
    const total = charts.remuneracion_total || {};
    const personas = charts.personas || {};
    const promedio = charts.remuneracion_promedio || {};
    const ratio = charts.remuneraciones_ventas || {};
    return {
        group: "personas",
        monthHeaders,
        // Sentinel del desglose "por equipo" = por CUENTA RRHH del prorrateo
        // (null si la empresa no clasifica sus docs de remuneración por
        // cuenta → el switch Equipos se oculta).
        teamCategory: data.team_category || null,
        remuneracionTotal: {
            total: total.total || [],
            porEquipo: total.por_equipo || {},
        },
        personas: {
            total: personas.total || [],
            porEquipo: personas.por_equipo || {},
        },
        remuneracionPromedio: {
            total: promedio.total || [],
            porEquipo: promedio.por_equipo || {},
        },
        remuneracionesVentas: {
            totalPct: ratio.total_pct || [],
            porEquipoPct: ratio.por_equipo_pct || {},
        },
    };
}

function normalizeTerceros(data, monthHeaders) {
    const charts = data.charts || {};
    const normalizeKind = (kind) => ({
        amount: kind.amount || [],
        count: kind.count || [],
        promedio: kind.promedio || [],
        ventasPct: kind.ventas_pct || [],
    });
    return {
        group: "terceros",
        monthHeaders,
        proveedores: normalizeKind(charts.proveedores || {}),
        honorarios: normalizeKind(charts.honorarios || {}),
    };
}

function normalizeMargenes(data, monthHeaders) {
    const charts = data.charts || {};
    const cliente = charts.promedio_cliente || {};
    const margenes = charts.margenes_pct || {};
    const persona = charts.promedio_persona || {};
    const gastos = charts.gastos_ventas_pct || {};
    // Series null = no aplican al super tipo (Servicios sin margen producto /
    // contribución) — el tab las omite.
    return {
        group: "margenes",
        monthHeaders,
        superType: data.super_type || null,
        promedioCliente: {
            arpa: cliente.arpa || [],
            ampa: cliente.ampa || [],
            acmpa: cliente.acmpa || null,
            aepa: cliente.aepa || [],
        },
        margenesPct: {
            producto: margenes.producto || null,
            bruto: margenes.bruto || [],
            contribucion: margenes.contribucion || null,
            ebitda: margenes.ebitda || [],
            neto: margenes.neto || [],
        },
        promedioPersona: {
            venta: persona.venta || [],
            margenBruto: persona.margen_bruto || [],
            margenContribucion: persona.margen_contribucion || null,
            ebitda: persona.ebitda || [],
        },
        gastosVentasPct: {
            total: gastos.total || [],
            administracion: gastos.administracion || [],
            comerciales: gastos.comerciales || [],
            iD: gastos.i_d || [],
        },
    };
}

export function normalizeKpisResultados(data) {
    // Los headers se formatean con la granularidad que respondió el backend
    // (mes/trimestre/año — misma etiqueta que el resto de los tabs).
    const monthHeaders = (data.period_labels || []).map(
        (iso) => formatPeriodLabel(iso, data.period || "month"),
    );
    if (data.group === "personas") {
        return normalizePersonas(data, monthHeaders);
    }
    if (data.group === "terceros") {
        return normalizeTerceros(data, monthHeaders);
    }
    if (data.group === "margenes") {
        return normalizeMargenes(data, monthHeaders);
    }
    const charts = data.charts || {};
    const ventas = charts.ventas_crecimiento || {};
    const clientes = charts.clientes_ticket || {};
    const flujo = charts.flujo_clientes || {};
    const ratios = charts.flujo_ratios || {};
    // G3/G4 (flujo de clientes) tienen su PROPIA ventana: el backend la
    // clampea al mes anterior (el mes en curso a medias distorsiona
    // nuevos/salientes). Sus headers salen de flujo_period_labels; fallback a
    // los generales por si el backend aún no manda el campo.
    const flujoMonthHeaders = (data.flujo_period_labels || data.period_labels || []).map(
        (iso) => formatPeriodLabel(iso, data.period || "month"),
    );
    return {
        group: data.group,
        monthHeaders,
        flujoMonthHeaders,
        ventasCrecimiento: {
            ventas: ventas.ventas || [],
            momPct: toPercentScale(ventas.mom_pct),
            yoyPct: toPercentScale(ventas.yoy_pct),
        },
        clientesTicket: {
            clientes: clientes.clientes || [],
            arpa: clientes.arpa || [],
            recurrentes: clientes.recurrentes || [],
            arrpa: clientes.arrpa || [],
        },
        flujoClientes: {
            nuevos: flujo.nuevos || [],
            salientes: flujo.salientes || [],
            nuevosAmount: flujo.nuevos_amount || [],
            salientesAmount: flujo.salientes_amount || [],
        },
        flujoRatios: {
            nuevosPct: ratios.nuevos_pct || [],
            salientesPct: ratios.salientes_pct || [],
            nuevosAmountPct: ratios.nuevos_amount_pct || [],
            salientesAmountPct: ratios.salientes_amount_pct || [],
        },
    };
}

const state = () => ({
    kpisData: null,
    isLoading: false,
    error: null,
});

const mutations = {
    SET_KPIS_DATA(state, payload) {
        state.kpisData = payload;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    // Fetch de las series del grupo activo para el rango que el tab define
    // localmente. Las series son siempre mensuales; el rango/corte lo decide
    // el filtro del header (resolveDateRange, mismo criterio que el resto).
    async fetchKpisResultados({ commit, rootState }, {
        endMonth,
        selectedPeriodRange,
        customRange = null,
        customYearRange = null,
        period = "month",
        group = "ventas",
    } = {}) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { initDate, endDate } = resolveDateRange(
                endMonth,
                selectedPeriodRange,
                period,
                customRange,
                customYearRange,
                rootState.reportsFilters.dataFirstMonth,
            );
            const response = await getKpisResultados(initDate, endDate, group, period);
            commit("SET_KPIS_DATA", normalizeKpisResultados(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[kpisReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar los KPIs."));
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
